import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { Box, IconButton, InputAdornment, Popover, Stack } from '@mui/material';
import type { PopoverActions } from '@mui/material/Popover';
import { Clear, KeyboardArrowDown } from '@mui/icons-material';
import type { AccessCentreRole, AccessCentreUser, AssignUserRoleApiResponse } from 'domains/accessCentre/types';
import SearchField from 'domains/core/components/SearchField';
import TruncatedTextWithTooltip from 'domains/core/components/TruncatedTextWithTooltip';
import ErrorSnackbar from 'domains/core/components/Snackbars/ErrorSnackbar';
import SuccessSnackbar from 'domains/core/components/Snackbars/SuccessSnackbar';
import useAssignUserRole from 'hooks/mutations/useAssignUserRole';
import useGetRolesList from 'hooks/queries/useGetRolesList';
import useDebounce from 'hooks/useDebounce';
import { SEARCH_DEBOUNCE_MS } from 'utils';
import { themeColors } from 'colors';
import RolePopoverListSection from './RolePopoverListSection';

const ROLES_LIST_LIMIT = 100;
const ROLE_POPOVER_MAX_HEIGHT_PX = 400;
const ROLE_POPOVER_WIDTH_PX = 308;

const roleCellStackSx = {
    minWidth: 0,
    maxWidth: '80%',
    width: '100%',
    typography: 'body1',
    fontFamily: 'inherit',
};

const roleNameBoxSx = {
    flex: '0 1 auto',
    minWidth: 0,
    overflow: 'hidden',
    maxWidth: '100%',
};

const roleTriggerIconButtonSx = {
    flexShrink: 0,
    p: 0.25,
};

const rolePopoverSearchContainerSx = {
    p: 0.5,
    pb: 1,
    flexShrink: 0,
};

const roleSearchFieldSx = {
    minWidth: 0,
    maxWidth: '100%',
    marginRight: 0,
    '& .MuiInputBase-input': {
        typography: 'body1',
        fontFamily: 'inherit',
        '&::placeholder': {
            typography: 'body2',
            fontFamily: 'inherit',
            opacity: 1,
            color: 'text.secondary',
        },
    },
    '& .MuiOutlinedInput-root': {
        '& .MuiOutlinedInput-notchedOutline': {
            borderColor: themeColors.action.active,
        },
        '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: themeColors.action.active,
        },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: themeColors.action.active,
            borderWidth: 2,
        },
    },
};

const rolePopoverPaperSxBase = {
    minWidth: ROLE_POPOVER_WIDTH_PX,
    maxWidth: ROLE_POPOVER_WIDTH_PX,
    maxHeight: ROLE_POPOVER_MAX_HEIGHT_PX,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
};

const rolePopoverPaperSxBelow = {
    ...rolePopoverPaperSxBase,
    mt: 0.5,
};

const rolePopoverPaperSxAbove = {
    ...rolePopoverPaperSxBase,
    mb: 0.5,
};

export type UsersTableRoleSelectionCellProps = {
    user: AccessCentreUser;
    showChangeRole?: boolean;
};

type RoleAssignSuccessSnapshot = {
    userName: string;
    prevRoleName: string;
    newRoleName: string;
};

const UsersTableRoleSelectionCell = ({ user, showChangeRole = false }: UsersTableRoleSelectionCellProps) => {
    const listboxId = useId();
    const roleCellRowRef = useRef<HTMLDivElement>(null);
    const popoverActionsRef = useRef<PopoverActions | null>(null);
    /** Holds prev/new role labels for the success toast (not React state). Set in mutate onSuccess. */
    const roleAssignSuccessSnapshotRef = useRef<RoleAssignSuccessSnapshot | null>(null);
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [popoverPlacement, setPopoverPlacement] = useState<'below' | 'above'>('below');
    const open = Boolean(anchorEl);
    const [searchValue, setSearchValue] = useState('');
    const debouncedSearch = useDebounce(searchValue, SEARCH_DEBOUNCE_MS);

    const { data: rolesData, isLoading: isRolesLoading } = useGetRolesList({
        limit: ROLES_LIST_LIMIT,
        offset: 0,
        searchTerm: debouncedSearch.trim() || undefined,
        reactQueryOptions: { enabled: open },
    });

    const {
        mutate: assignMutate,
        data: assignRoleData,
        isError: isAssignRoleError,
        isSuccess: isAssignRoleSuccess,
        error: assignRoleError,
        isLoading: isAssignRoleLoading,
        reset: resetAssignRoleMutation,
    } = useAssignUserRole(user.id);

    const assignRoleSuccessMessage = useMemo(() => {
        if (!isAssignRoleSuccess || !assignRoleData || !roleAssignSuccessSnapshotRef.current) return null;
        const { userName, prevRoleName, newRoleName } = roleAssignSuccessSnapshotRef.current;
        return `${userName}'s role updated ${prevRoleName} → ${newRoleName}`;
    }, [isAssignRoleSuccess, assignRoleData]);

    useEffect(() => {
        if (!open) {
            setSearchValue('');
        }
    }, [open]);

    /** Keep popover aligned with the role cell when the table or page scrolls (MUI does not do this by default). */
    useEffect(() => {
        if (!open) return undefined;

        let rafId = 0;
        const updatePosition = () => {
            cancelAnimationFrame(rafId);
            rafId = requestAnimationFrame(() => {
                popoverActionsRef.current?.updatePosition();
            });
        };

        document.addEventListener('scroll', updatePosition, true);
        window.addEventListener('resize', updatePosition);
        return () => {
            cancelAnimationFrame(rafId);
            document.removeEventListener('scroll', updatePosition, true);
            window.removeEventListener('resize', updatePosition);
        };
    }, [open]);

    const roles = rolesData?.data ?? [];

    const handleOpen = () => {
        resetAssignRoleMutation();
        roleAssignSuccessSnapshotRef.current = null;
        const el = roleCellRowRef.current;
        if (!el) return;
        const rect = el.getBoundingClientRect();
        const spaceBelow = window.innerHeight - rect.bottom;
        const spaceAbove = rect.top;
        const openAbove = spaceBelow < ROLE_POPOVER_MAX_HEIGHT_PX && spaceAbove > spaceBelow;
        setPopoverPlacement(openAbove ? 'above' : 'below');
        setAnchorEl(el);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleSelectRole = (role: AccessCentreRole) => {
        if (role.id === user.role_id) {
            handleClose();
            return;
        }
        const prevRoleName = user?.role_name ?? '';
        assignMutate(
            { roleId: role.id },
            {
                onSuccess: (data: AssignUserRoleApiResponse) => {
                    const updated = data?.user;
                    roleAssignSuccessSnapshotRef.current = {
                        userName: updated?.name ?? user?.name ?? '',
                        prevRoleName,
                        newRoleName: updated?.role_name ?? role?.name,
                    };
                    handleClose();
                },
            }
        );
    };

    if (!showChangeRole) {
        return <TruncatedTextWithTooltip value={user.role_name ?? ''} />;
    }

    return (
        <>
            {assignRoleSuccessMessage && (
                <SuccessSnackbar key={assignRoleSuccessMessage} successMessage={assignRoleSuccessMessage} />
            )}
            {isAssignRoleError && assignRoleError && <ErrorSnackbar errorMessage={assignRoleError?.message ?? ''} />}
            <Stack
                ref={roleCellRowRef}
                direction="row"
                alignItems="center"
                spacing={0.5}
                justifyContent="flex-start"
                sx={roleCellStackSx}
            >
                <Box sx={roleNameBoxSx}>
                    <TruncatedTextWithTooltip value={user.role_name ?? ''} maxWidth="100%" />
                </Box>
                <IconButton
                    size="small"
                    color="inherit"
                    onClick={handleOpen}
                    disabled={isAssignRoleLoading}
                    aria-haspopup="listbox"
                    aria-expanded={open}
                    aria-controls={open ? listboxId : undefined}
                    aria-label={`Open role selection, current role ${user.role_name ?? ''}`}
                    data-testid="access-center-user-role-trigger"
                    sx={roleTriggerIconButtonSx}
                >
                    <KeyboardArrowDown fontSize="inherit" />
                </IconButton>
            </Stack>
            <Popover
                action={popoverActionsRef}
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={
                    popoverPlacement === 'below'
                        ? { vertical: 'bottom', horizontal: 'left' }
                        : { vertical: 'top', horizontal: 'left' }
                }
                transformOrigin={
                    popoverPlacement === 'below'
                        ? { vertical: 'top', horizontal: 'left' }
                        : { vertical: 'bottom', horizontal: 'left' }
                }
                PaperProps={{
                    elevation: 8,
                    sx: popoverPlacement === 'below' ? rolePopoverPaperSxBelow : rolePopoverPaperSxAbove,
                }}
                disableScrollLock
            >
                <Box sx={rolePopoverSearchContainerSx}>
                    <SearchField
                        ignoreSearchFeatureFlag
                        hideSearchIcon
                        variant="outlined"
                        placeholder="Search"
                        value={searchValue}
                        onChange={setSearchValue}
                        dataTestId="access-center-user-role-search"
                        autoFocus
                        fullWidth
                        inputProps={{ 'aria-label': 'Search roles' }}
                        endAdornment={
                            searchValue ? (
                                <InputAdornment position="end">
                                    <IconButton
                                        size="small"
                                        aria-label="Clear search"
                                        onClick={() => setSearchValue('')}
                                        edge="end"
                                    >
                                        <Clear fontSize="small" />
                                    </IconButton>
                                </InputAdornment>
                            ) : undefined
                        }
                        sx={roleSearchFieldSx}
                    />
                </Box>
                <RolePopoverListSection
                    isRolesLoading={isRolesLoading}
                    roles={roles}
                    currentRoleId={user.role_id}
                    listboxId={listboxId}
                    selectDisabled={isAssignRoleLoading}
                    onSelectRole={handleSelectRole}
                />
            </Popover>
        </>
    );
};

export default UsersTableRoleSelectionCell;
